import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { cx } from "@/lib/utils";

interface StepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  testId?: string;
  /**
   * KAN-1903 — called with the value the user TYPED when it was above
   * ``max`` (the field still clamps). Without it the clamp was silent:
   * 10 000 became 9 999 and nobody was told why.
   */
  onExceedMax?: (attempted: number) => void;
}

export function Stepper({
  value,
  onChange,
  min = 1,
  max = 10000,
  step = 1,
  disabled = false,
  ariaLabel,
  className,
  testId,
  onExceedMax,
}: StepperProps): JSX.Element {
  const { t } = useTranslation("common");
  const clamp = useCallback(
    (n: number) => Math.max(min, Math.min(max, n)),
    [min, max],
  );

  const dec = () => onChange(clamp(value - step));
  const inc = () => onChange(clamp(value + step));

  return (
    <div
      className={cx(
        "inline-flex items-center rounded-md border border-border-strong bg-surface",
        disabled && "opacity-60",
        className,
      )}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={dec}
        disabled={disabled || value <= min}
        aria-label={t("stepper.decrease", { label: ariaLabel ?? t("stepper.value") })}
        className="flex h-9 w-9 items-center justify-center text-ink-2 hover:bg-surface-2 disabled:cursor-not-allowed disabled:text-ink-4"
      >
        −
      </button>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        aria-label={ariaLabel ?? t("stepper.value")}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          if (n > max) onExceedMax?.(n);
          onChange(clamp(n));
        }}
        className="mono h-9 w-14 border-x border-border bg-transparent text-center text-sm tabular-nums focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button
        type="button"
        onClick={inc}
        disabled={disabled || value >= max}
        aria-label={t("stepper.increase", { label: ariaLabel ?? t("stepper.value") })}
        className="flex h-9 w-9 items-center justify-center text-ink-2 hover:bg-surface-2 disabled:cursor-not-allowed disabled:text-ink-4"
      >
        +
      </button>
    </div>
  );
}
